/**
 * Catálogo de medicamentos (camada de acesso do frontend).
 *
 * Alimenta o autocomplete do receituário estruturado com a base oficial
 * CMED/ANVISA importada no banco, e busca a sugestão de posologia.
 *
 * @see Backend/backend/src/services/clinicMedicationCatalogService.ts
 */

import axios from '../config/axios';
import { getApiUrl } from '../config/environment';
import { sortMedicationCatalogResults } from '../utils/medicationCatalogSort';

export { sortMedicationCatalogResults } from '../utils/medicationCatalogSort';

export interface MedicationCatalogEntry {
  id: string;
  product_name: string;
  active_ingredient: string | null;
  laboratory: string | null;
  presentation_raw: string | null;
  concentration: string | null;
  pharmaceutical_form: string | null;
  package_quantity: string | null;
  default_route: string | null;
  product_type: string | null;
  is_generic: boolean;
  is_controlled: boolean;
  control_class: string | null;
  is_antimicrobial: boolean;
  sncr_receita_tipo?: string | null;
  anvisa_registration: string | null;
  ean_code: string | null;
  pmc_price: number | null;
  source: string;
  posology_profile_key: string | null;
  has_verified_posology?: boolean;
  score?: number;
}

export interface PosologySuggestion {
  dosage: string | null;
  frequency: string | null;
  route: string | null;
  duration: string | null;
  quantity: string | null;
  instructions: string | null;
  is_controlled?: boolean;
  control_class?: string | null;
  is_antimicrobial?: boolean;
  sncr_receita_tipo?: string | null;
  times_used?: number;
  last_used_at?: string | null;
  verification?: {
    source: 'anvisa_leaflet';
    registration: string;
    url: string | null;
    verified_at: string;
    reviewed_by: string;
  };
}

export type PosologySource =
  | 'verified_bula'
  | 'cmed_draft'
  | 'professional_history'
  | 'unit_history'
  | 'ai'
  | 'none';

export interface PosologyResult {
  source: PosologySource;
  suggestions: PosologySuggestion[];
  notice: string | null;
}

export interface MedicationCatalogStatus {
  available: boolean;
  total: number;
  last_import: { reference_date: string | null; finished_at: string | null } | null;
}

/** Status compartilhado: evita uma requisição por linha de medicamento. */
let statusPromise: Promise<MedicationCatalogStatus> | null = null;

export function invalidateMedicationCatalogCaches(): void {
  statusPromise = null;
}

export function fetchMedicationCatalogStatus(opts?: { force?: boolean }): Promise<MedicationCatalogStatus> {
  if (opts?.force) statusPromise = null;
  if (!statusPromise) {
    statusPromise = axios
      .get(getApiUrl('/clinic/medication-catalog/status'))
      .then((response) => response.data?.data as MedicationCatalogStatus)
      .catch(() => ({ available: false, total: 0, last_import: null }));
  }
  return statusPromise;
}

export async function searchMedicationCatalog(
  terms: string,
  limit = 25,
): Promise<MedicationCatalogEntry[]> {
  const { data } = await axios.get(getApiUrl('/clinic/medication-catalog/search'), {
    params: { terms, limit },
  });
  const rows: MedicationCatalogEntry[] = Array.isArray(data?.data) ? data.data : [];
  return sortMedicationCatalogResults(rows);
}

export async function fetchPosologySuggestion(payload: {
  medication_name: string;
  active_ingredient?: string | null;
  concentration?: string | null;
  pharmaceutical_form?: string | null;
  professional_id?: string | null;
  posology_profile_key?: string | null;
  catalog_id?: string | null;
}): Promise<PosologyResult> {
  const { data } = await axios.post(getApiUrl('/clinic/medication-catalog/posology'), payload);
  return (data?.data as PosologyResult) || { source: 'none', suggestions: [], notice: null };
}

export interface CmedImportResult {
  rows_read: number;
  rows_upserted: number;
  parse_coverage: number;
  form_coverage: number;
  concentration_coverage: number;
  reference_date: string;
  source_file: string;
}

/** Importa a lista de conformidade CMED/ANVISA (.xlsx / .xls). */
export async function importCmedCatalog(file: File): Promise<CmedImportResult> {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await axios.post(getApiUrl('/clinic/medication-catalog/import-cmed'), formData, {
    timeout: 20 * 60 * 1000,
  });
  return data?.data as CmedImportResult;
}

/** Rótulo curto da apresentação para a lista do autocomplete. */
export function formatPresentation(entry: MedicationCatalogEntry): string {
  return [entry.concentration, entry.pharmaceutical_form, entry.package_quantity]
    .filter(Boolean)
    .join(' · ');
}
